import { DateTime } from 'luxon'
import { isTodayOrLater } from 'customer-flows/availability/ranges'

export default function hydrateDate({ stores, dispatch, services }: CommandArgs) {
  const flowRouter: FlowRouter = services['flow-router']
  const availabilityStore: AvailabilityStore = stores['availability']
  const bookingFlowStore: BookingFlowStore = stores['program/booking-flow']

  /**
   * Resolves an initial date from query params or program config, then fetches
   * availability for it. Sets `dateHydratedFromParam` so the date-picker knows
   * whether to collapse on mount.
   *
   * Priority: ?date param > programCalendarStartDate
   * No-op if no date resolves — the date-picker will fetch its own availability on mount.
   */
  async function hydrate() {
    const dateFromParam = resolveFromParams()
    const date = dateFromParam ?? resolveFromConfig({
      programCalendarStartDate: bookingFlowStore.calendarStartDate
    })

    if (!date) return

    // flag is read by the date-picker to decide whether to collapse the section
    if (dateFromParam) {
      availabilityStore.dateHydratedFromParam = true
    }

    await dispatch('availability:setSelectedDate', dateFromParam, { calendarStartDate: date })
  }

  /**
   * Returns the ?date query param if it is a valid ISO date string, otherwise undefined.
   */
  function resolveFromParams(): string | undefined {
    const date = flowRouter.queryParams?.date
    if (!date) return undefined

    const parsed = DateTime.fromISO(date)
    return parsed.isValid ? date : undefined
  }

  /**
   * Returns the first config-supplied date that is today or in the future.
   * Past dates are ignored — they would show an empty calendar.
   */
  function resolveFromConfig({ programCalendarStartDate }: {
    programCalendarStartDate?: string
  }): string | undefined {
    if (programCalendarStartDate && isTodayOrLater(programCalendarStartDate)) return programCalendarStartDate
    return undefined
  }

  return { hydrate, resolveFromParams, resolveFromConfig }
}

declare global {
  export type HydrateDateCommands = ReturnType<typeof hydrateDate>

  interface CommandRegistry {
    'hydrate/date': HydrateDateCommands
  }
}
